// ===================== Title scene — animated key-art backdrop =====================
// Drawn on the game canvas behind the title/select menus: a deterministic
// synthwave vista — twinkling starfield, banded sun, ridge silhouettes and a
// scrolling perspective grid — with neon bullet streaks and drifting embers
// for bullet-heaven flavor. Pure procedural canvas: no assets, consistent with
// the game's zero-dependency art pipeline. Honors reduced-motion (freezes).
import { Settings } from '../engine/settings.js';
import { hash2, TAU, clamp01 } from '../engine/math.js';

const CYAN = '#7df9ff';
const MAGENTA = '#ff5cf0';
const GOLD = '#ffd15c';

// Gradients are rebuilt only when the canvas size changes.
let _key = '', _sky = null, _vig = null, _sun = null;
function ensureGradients(ctx, w, h, horizon) {
  const key = w + 'x' + h;
  if (key === _key) return;
  _key = key;

  _sky = ctx.createLinearGradient(0, 0, 0, h);
  _sky.addColorStop(0, '#04040c');
  _sky.addColorStop(0.55, '#0b0d24');
  _sky.addColorStop(Math.min(1, horizon / h), '#251142');
  _sky.addColorStop(1, '#0a0a18');

  _vig = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.35, w / 2, h / 2, Math.max(w, h) * 0.74);
  _vig.addColorStop(0, 'rgba(0,0,0,0)');
  _vig.addColorStop(1, 'rgba(0,0,0,0.6)');

  const r = Math.min(w, h) * 0.26;
  _sun = ctx.createLinearGradient(0, horizon - r, 0, horizon + r * 0.2);
  _sun.addColorStop(0, GOLD);
  _sun.addColorStop(0.55, '#ff8a3d');
  _sun.addColorStop(1, MAGENTA);
}

export function renderTitleScene(ctx, w, h, time) {
  const t = Settings.reducedMotion ? 0 : time;
  const horizon = h * 0.64;
  ensureGradients(ctx, w, h, horizon);

  // Sky.
  ctx.fillStyle = _sky;
  ctx.fillRect(0, 0, w, h);

  // Starfield — deterministic grid hash, gentle twinkle.
  const S = 90;
  for (let gx = 0; gx * S < w + S; gx++) {
    for (let gy = 0; gy * S < horizon; gy++) {
      const r1 = hash2(gx, gy, 3);
      if (r1 > 0.62) continue;
      const x = gx * S + hash2(gx, gy, 11) * S;
      const y = gy * S + hash2(gx, gy, 23) * S;
      if (y > horizon - 20) continue;
      const tw = 0.35 + 0.65 * Math.abs(Math.sin(t * (0.4 + r1) + r1 * 20));
      ctx.globalAlpha = (0.16 + r1 * 0.5) * tw;
      ctx.fillStyle = r1 < 0.14 ? CYAN : '#dfe6ff';
      const sz = r1 < 0.1 ? 2.4 : 1.4;
      ctx.fillRect(x, y, sz, sz);
    }
  }
  ctx.globalAlpha = 1;

  // Banded synthwave sun on the horizon.
  const sunR = Math.min(w, h) * 0.26;
  ctx.save();
  ctx.beginPath();
  ctx.arc(w / 2, horizon, sunR, Math.PI, 0);
  ctx.closePath();
  ctx.clip();
  ctx.fillStyle = _sun;
  ctx.fillRect(w / 2 - sunR, horizon - sunR, sunR * 2, sunR);
  // Slats widen toward the horizon line.
  ctx.fillStyle = '#0b0d24';
  for (let i = 0; i < 6; i++) {
    const yy = horizon - sunR * (0.52 - i * 0.1);
    ctx.fillRect(w / 2 - sunR, yy, sunR * 2, 1.5 + i * 1.3);
  }
  ctx.restore();
  // Sun halo.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const halo = ctx.createRadialGradient(w / 2, horizon, sunR * 0.2, w / 2, horizon, sunR * 1.9);
  halo.addColorStop(0, 'rgba(255,92,240,0.20)');
  halo.addColorStop(1, 'rgba(255,92,240,0)');
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();

  // Ridge silhouettes (two parallax-flavored layers, deterministic).
  ridge(ctx, w, horizon, 17, 0.16, '#141031');
  ridge(ctx, w, horizon, 29, 0.09, '#0d0b22');

  // Perspective grid floor, rows scrolling toward the viewer.
  ctx.save();
  ctx.strokeStyle = 'rgba(125,249,255,0.30)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  const cols = 26;
  for (let i = -cols; i <= cols; i++) {
    const xb = w / 2 + (i / cols) * w * 1.7;   // spread at the bottom edge
    const xt = w / 2 + (i / cols) * w * 0.06;  // converge at the horizon
    ctx.moveTo(xt, horizon);
    ctx.lineTo(xb, h);
  }
  ctx.stroke();
  const rows = 11;
  for (let k = 0; k < rows; k++) {
    const zt = (k / rows + t * 0.12) % 1;
    const y = horizon + (h - horizon) * zt * zt;
    ctx.globalAlpha = 0.12 + zt * 0.5;
    ctx.strokeStyle = k % 3 === 0 ? 'rgba(255,92,240,0.5)' : 'rgba(125,249,255,0.4)';
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
  }
  // Horizon glow line.
  ctx.globalAlpha = 1;
  ctx.strokeStyle = 'rgba(255,92,240,0.75)';
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.moveTo(0, horizon); ctx.lineTo(w, horizon); ctx.stroke();
  ctx.restore();

  // Drifting embers rising off the grid.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < 22; i++) {
    const r1 = hash2(i, 5, 13), r2 = hash2(i, 9, 29);
    const rise = ((t * (0.05 + r2 * 0.05) + r1) % 1);
    const x = r1 * w + Math.sin(t * 0.5 + i) * 26;
    const y = h - rise * h * 0.85;
    ctx.globalAlpha = 0.35 * Math.sin(Math.PI * rise);
    ctx.fillStyle = i % 3 === 0 ? MAGENTA : CYAN;
    const sz = 1.6 + r2 * 2.2;
    ctx.fillRect(x, y, sz, sz);
  }
  ctx.restore();

  // Neon bullet streaks crossing the sky (the "bullet heaven" bit).
  if (!Settings.reducedMotion) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < 4; i++) {
      const r1 = hash2(i, 3, 7), r2 = hash2(i, 8, 19);
      const period = 4.5 + r1 * 4;
      const ph = ((t / period) + r2) % 1;
      const ltr = i % 2 === 0;             // alternate travel direction
      const px = ltr ? ph * (w + 240) - 120 : w + 120 - ph * (w + 240);
      const py = h * (0.10 + r1 * 0.42) + Math.sin(ph * 9 + i) * 14;
      const a = Math.sin(Math.PI * clamp01(ph)) * 0.9;
      if (a <= 0.02) continue;
      const col = i % 3 === 0 ? GOLD : i % 2 === 0 ? CYAN : MAGENTA;
      const dir = ltr ? 1 : -1;
      ctx.globalAlpha = a;
      for (let s = 0; s < 3; s++) {
        ctx.globalAlpha = a * (1 - s * 0.32);
        ctx.fillStyle = col;
        ctx.beginPath();
        ctx.ellipse(px - dir * s * 22, py, 14 - s * 3.4, 4.4 - s, 0, 0, TAU);
        ctx.fill();
      }
      ctx.globalAlpha = a;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(px + dir * 6, py, 2.6, 0, TAU); ctx.fill();
    }
    ctx.restore();
  }

  // Vignette.
  ctx.fillStyle = _vig;
  ctx.fillRect(0, 0, w, h);
}

// Jagged deterministic ridge line silhouette in front of the sun.
function ridge(ctx, w, horizon, seed, height, color) {
  const step = Math.max(26, w / 34);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(-4, horizon + 1);
  for (let x = -4, i = 0; x <= w + step; x += step, i++) {
    const hh = hash2(i, seed, seed) * height + hash2(i, seed + 1, 3) * height * 0.4;
    ctx.lineTo(x, horizon - hh * horizon * 0.6);
  }
  ctx.lineTo(w + 4, horizon + 1);
  ctx.closePath();
  ctx.fill();
}
